/** Unified Pointer Events → InputSample, with coalesced-event recovery. */
import type { Engine } from '../core/engine';
import type { InputSample, PointerType } from '../core/module-api';

export function attachPointer(canvas: HTMLCanvasElement, engine: Engine): () => void {
  let drawing = false;
  let activeId: number | null = null;
  let last: { x: number; y: number; t: number } | null = null;
  let penActive = false;
  let lastPenTime = -1e9;

  const toSample = (e: PointerEvent): InputSample => {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const t = e.timeStamp;
    let speed = 0;
    if (last) {
      const dt = Math.max(1, t - last.t);
      speed = Math.hypot(x - last.x, y - last.y) / dt;
    }
    const pointerType = (e.pointerType || 'mouse') as PointerType;
    let pressure = e.pressure;
    if (pointerType === 'mouse') {
      // Pressure-from-speed fallback so pressure-driven modules degrade gracefully.
      pressure = Math.max(0.15, Math.min(1, 1 - speed / 4));
    } else if (pressure === 0 && e.buttons !== 0) {
      pressure = 0.5;
    }
    return {
      x,
      y,
      pressure,
      tiltX: e.tiltX || 0,
      tiltY: e.tiltY || 0,
      twist: e.twist || 0,
      speed,
      buttons: e.buttons,
      pointerType,
      timeStamp: t,
    };
  };

  const down = (e: PointerEvent): void => {
    if (e.pointerType === 'pen') {
      penActive = true;
      lastPenTime = e.timeStamp;
    } else if (e.pointerType === 'touch' && (penActive || e.timeStamp - lastPenTime < 700)) {
      // Palm rejection: ignore touch during, or shortly after, pen use.
      e.preventDefault();
      return;
    }
    if (activeId !== null) return;
    drawing = true;
    activeId = e.pointerId;
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    last = null;
    const s = toSample(e);
    last = { x: s.x, y: s.y, t: s.timeStamp };
    engine.pointerDown(s);
    e.preventDefault();
  };

  const move = (e: PointerEvent): void => {
    if (e.pointerType === 'pen') lastPenTime = e.timeStamp;
    if (!drawing || e.pointerId !== activeId) {
      // Hover (no button) drives inverted-interaction tools like InverseShapes.
      // Mouse only: a hovering pen would record air-movement as drawing.
      if (!drawing && e.buttons === 0 && e.pointerType === 'mouse') engine.pointerHover(toSample(e));
      return;
    }
    const coalesced =
      typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
    const events = coalesced.length ? coalesced : [e];
    for (const ce of events) {
      const s = toSample(ce);
      engine.pointerMove(s);
      last = { x: s.x, y: s.y, t: s.timeStamp };
    }
    e.preventDefault();
  };

  const up = (e: PointerEvent): void => {
    if (e.pointerType === 'pen') {
      penActive = false;
      lastPenTime = e.timeStamp;
    }
    if (e.pointerId !== activeId) return;
    const s = toSample(e);
    engine.pointerUp(s);
    drawing = false;
    activeId = null;
    last = null;
    try {
      canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    e.preventDefault();
  };

  canvas.addEventListener('pointerdown', down);
  canvas.addEventListener('pointermove', move);
  canvas.addEventListener('pointerup', up);
  canvas.addEventListener('pointercancel', up);

  return () => {
    canvas.removeEventListener('pointerdown', down);
    canvas.removeEventListener('pointermove', move);
    canvas.removeEventListener('pointerup', up);
    canvas.removeEventListener('pointercancel', up);
  };
}
